import React, { useState, useEffect, useRef, useContext } from "react";
import { Context } from "Context";
import useFetch from "hooks/useFetch";
import useKeyPress, { EVENT_KEYS } from "hooks/useKeyPress";
import Icon from "components/shared/Icon";
import Link from "components/shared/Link";
import Button from "components/shared/Button";
import { mapString } from "utils/common";
import { getCookie } from "utils/cookie";
import { Fragment } from "react";
import { useParams } from "react-router-dom";
import AccessibleText from "components/shared/AccessibleText";
import DecorativeImage from "components/shared/DecorativeImage";

const { origin } = window.location;
const helpGroup = "help";
const cacheMinutes = 0;
const defaultUserType = "GUEST";
const ellipsis = "...";
const searchTimeout = 3000;
const conversationId =
	getCookie("PHPSESSID") ||
	getCookie("Conversation_UUID") ||
	getCookie("convId") ||
	"";

const Search = ({ search, swid, decorativeImage }) => {
	const _isMounted = useRef(true);
	const {
		config: {
			additionalSearchParams = "",
			basePath,
			cdnAssetsUrl,
			helpAssetsUrl,
			navigationBaseApiUrl,
			searchHistory,
			searchService,
			useNavigationSearchService = searchService.useNavigationSearchService,
			userTypesSearchResultsOpeningInNewTab,
		},
		profileData: { userType },
	} = useContext(Context);

	const { group, category, subcategory } = useParams();
	const imageLocation = `${helpAssetsUrl}/images`;
	const [searchQuery, setSearchQuery] = useState("");
	const [searchParams, setSearchParams] = useState(null);
	const [searchHistoryParams, setSearchHistoryParams] = useState(null);
	const [showSuggestions, setShowSuggestions] = useState(false);
	const [fairyImage, setFairyImage] = useState(null);
	const downPress = useKeyPress(EVENT_KEYS.arrowdown);
	const upPress = useKeyPress(EVENT_KEYS.arrowup);
	const [cursor, setCursor] = useState(-1);
	let refsColletion = {};
	const {
		baseParams: searchServiceBaseParams,
		useCurrentUserType,
		path: searchServicePath,
	} = searchService;
	const {
		baseParams: searchHistoryBaseParams,
		path: searchHistoryPath,
		queryParams,
	} = searchHistory;
	const searchServiceUrl = useNavigationSearchService
		? `${navigationBaseApiUrl}${searchServicePath}?${searchParams?.queryParams}`
		: `${basePath}${searchServicePath}`;
	const searchHistoryUrl = useNavigationSearchService
		? `${navigationBaseApiUrl}${searchHistoryPath}?${mapString(
				queryParams,
				userType
		  )}`
		: `${basePath}${searchHistoryPath}`;

	const { response } = useFetch(searchParams ? searchServiceUrl : "", {
		options: searchParams?.options,
		trigger: searchParams,
		cacheValidMinutes: cacheMinutes,
	});

	const { response: searchHistoryResponse, error: searchHistoryError } =
		useFetch(searchHistoryParams ? searchHistoryUrl : "", {
			options: searchHistoryParams,
			trigger: searchHistoryParams,
			cacheValidMinutes: cacheMinutes,
		});

	const decorativeMainClass ="help-main";

	const ref = useRef(null);

	const navigateToSearchResult = () => {
		if (cursor === -1) {
			return;
		}
		const searchResultUrl = response?.links?.[cursor]?.url;
		if (
			!searchResultUrl ||
			searchResultUrl === "" ||
			searchResultUrl === "/#"
		) {
			navigateToSearchPage();
		} else {
			if (userTypesSearchResultsOpeningInNewTab.includes(userType)) {
				window.open(searchResultUrl, "_blank");
			} else {
				window.location.assign(searchResultUrl);
			}
		}
	};

	const handleSelection = (event, position) => {
		if (position >= 0) {
			setCursor(position);
			event?.preventDefault();
			makeSearchHistoryRequest(position);
		}
	};

	const keyboardNavigation = (event) => {
		if (
			event.key !== "ArrowDown" &&
			event.key !== "ArrowUp" &&
			event.key !== "Enter"
		) {
			setCursor(-1);
		}

		if (event.key === "Enter") {
			if (cursor >= 0) {
				makeSearchHistoryRequest(cursor);
			} else {
				setTimeout(() => {
					navigateToSearchPage();
				}, searchTimeout);
			}
		}
	};

	const fullWordsEllipsis = (text = "") => {
		if (text.length > 40) {
			const fitText = text.substring(0, 36);
			text = fitText.substring(0, fitText.lastIndexOf(" ")) + ellipsis;
		}
		return text;
	};

	const extractIcon = (iconSource) => {
		return iconSource.split('<span class="icon__')[1].split(" ")[0];
	};

	const handleInnerClick = () => {
		if (searchQuery && response?.links?.length > 0) {
			setShowSuggestions(true);
		}
	};

	const getRandomInt = (max) => {
		return Math.floor(Math.random() * max);
	};

	const removeEllipsis = (title, desc) => {
		let wordWithEllipsis = title || desc || "";
		if (wordWithEllipsis.endsWith(ellipsis)) {
			return wordWithEllipsis.replace(ellipsis, "");
		}
		return wordWithEllipsis;
	};

	const selectFairyImage = (group, category, subcategory) => {
		const FAIRYIMAGES = [
			"godmother-all.png",
			"godmother-pink.png",
			"godmother-blue.png",
			"godmother-green.png",
		];

		if (group === helpGroup) {
			if (category) {
				return FAIRYIMAGES[getRandomInt(3) + 1];
			} else {
				return FAIRYIMAGES[0];
			}
		} else {
			if (subcategory) {
				return FAIRYIMAGES[2];
			} else {
				if (category) {
					return FAIRYIMAGES[3];
				} else {
					return FAIRYIMAGES[1];
				}
			}
		}
	};
	const navigateToSearchPage = () => {
		if (isSearchQueryValid(searchQuery)) {
			const searchPageUrl = `${origin}${basePath}/search/?searchQuery=${searchQuery
				.trim()
				.replace(/ +/g, "+")
				.replace("...", "")}${additionalSearchParams}`;
			if (userTypesSearchResultsOpeningInNewTab.includes(userType)) {
				window.open(searchPageUrl, "_blank");
			} else {
				window.location.assign(searchPageUrl);
			}
		}
	};
	const isSearchQueryValid = (searchQuery = "", allowedChars = 1) => {
		return searchQuery.replace(/  +/g, "").length > allowedChars;
	};

	const makeSearchHistoryRequest = (position) => {
		if (useNavigationSearchService) {
			setSearchHistoryParams({
				method: "PUT",
				body: JSON.stringify({
					title: response?.links?.[position]?.fullTitle,
					position,
					documentId: response?.links?.[position]?.id,
					searchId: response?.searchId,
				}),
				headers: {
					"Content-Type": "application/json",
					swid,
					"conversation-id": conversationId,
				},
			});
		} else {
			setSearchHistoryParams({
				method: "POST",
				body: mapString(
					searchHistoryBaseParams,
					response?.links?.[position]?.fullTitle,
					position,
					response?.links?.[position]?.id,
					response?.searchId
				),
				headers: {
					"Content-Type": "application/x-www-form-urlencoded;charset=UTF-8",
				},
			});
		}
	};

	useEffect(() => {
		if (showSuggestions && response?.links?.length && !upPress && downPress) {
			setCursor((prevState) => {
				return prevState < response?.links?.length - 1
					? prevState + 1
					: prevState;
			});
		}
	}, [downPress, response, showSuggestions, upPress]);

	useEffect(() => {
		if (showSuggestions && response?.links?.length && !downPress && upPress) {
			setCursor((prevState) => {
				return prevState > 0 ? prevState - 1 : prevState;
			});
		}
	}, [upPress, response, showSuggestions, downPress]);

	useEffect(() => {
		if (searchQuery === "") setCursor(-1);
	}, [searchQuery]);

	useEffect(() => {
		if (cursor === -1) return;
		setSearchQuery(
			removeEllipsis(
				response?.links?.[cursor]?.title,
				response?.links?.[cursor]?.desc
			)
		);
		refsColletion?.[cursor]?.focus();
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [cursor]);

	useEffect(() => {
		if (searchHistoryResponse || searchHistoryError) {
			navigateToSearchResult(cursor);
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [searchHistoryResponse, searchHistoryError]);

	useEffect(() => {
		if (!userType) return;
		if (cursor !== -1) return;
		const loadResultsTimeout = setTimeout(() => {
			const validSearchQuery = searchQuery.trim().replace(/  +/g, " ");
			if (isSearchQueryValid(validSearchQuery)) {
				const pageName = window.location.pathname
					.split("/")
					.filter((part) => part.length)
					.join("_");
				const searchServiceParams = mapString(
					searchServiceBaseParams,
					useCurrentUserType ? userType : defaultUserType,
					pageName,
					validSearchQuery
				);
				if (useNavigationSearchService) {
					setSearchParams({
						queryParams: searchServiceParams,
						options: { headers: { swid, "conversation-id": conversationId } },
					});
				} else {
					setSearchParams({
						options: {
							method: "POST",
							body: searchServiceParams,
							headers: {
								"Content-Type":
									"application/x-www-form-urlencoded;charset=UTF-8",
							},
						},
					});
				}
				setCursor(-1);
			} else if (validSearchQuery.length === 0) {
				setShowSuggestions(false);
			}
		}, 600);
		return () => {
			clearTimeout(loadResultsTimeout);
		};
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [
		searchQuery,
		searchServiceBaseParams,
		swid,
		useCurrentUserType,
		userType,
		useNavigationSearchService,
	]);

	useEffect(() => {
		if (response && response.links?.length > 0 && searchQuery) {
			setShowSuggestions(true);
		} else if (!response || response.links?.length === 0) {
			setShowSuggestions(false);
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [response]);

	useEffect(() => {
		setFairyImage(selectFairyImage(group, category, subcategory));
		const handleClickOutside = (event) => {
			if (!ref.current?.contains(event.target) && _isMounted.current) {
				setShowSuggestions(false);
			}
		};
		document.addEventListener("click", handleClickOutside, true);
		return () => {
			window.removeEventListener("click", handleClickOutside, true);
			_isMounted.current = false;
		};
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, []);

	return (
		<Fragment>
			<div aria-live="polite" ref={ref} className="help-search">
				<div
					className={
						"help-search__form" +
						(showSuggestions ? " help-search__form--focus" : "")
					}
				>
					<Button
						accessibleText={search?.confirmSearch}
						className="help-icon help-icon--search"
						customStyles
						onClick={navigateToSearchPage}
						style={{
							backgroundImage: `url(${cdnAssetsUrl}/icons/pep/search.svg)`,
						}}
					/>

					<input
						aria-label={search?.placeholder}
						onClick={handleInnerClick}
						className="help-search__form__input"
						type="text"
						value={searchQuery}
						onChange={(event) => setSearchQuery(event.target.value)}
						placeholder={search?.placeholder}
						maxLength="254"
						autoComplete="off"
						name="searchQuery"
						onKeyDown={keyboardNavigation}
					/>
					<Button
						accessibleText={search?.clearSearch}
						className="help-icon help-icon--close-button"
						customStyles
						disabled={!searchQuery}
						onClick={() => setSearchQuery("")}
						style={{
							backgroundImage: `url(${cdnAssetsUrl}/icons/pep/close-button.svg)`,
						}}
					/>
				</div>
				<AccessibleText ariaLive="assertive">
					{searchQuery &&
						showSuggestions &&
						mapString(
							search?.accessibilityTextSearchResults,
							response?.links?.length || 0
						)}
				</AccessibleText>
				{showSuggestions && searchQuery && (
					<ul className="help-search__autosuggest">
						<li className="help-search__autosuggest__list-item">
							{search?.searchResults}
						</li>
						{response?.links?.map((link, i) => (
							<li
								key={link.id}
								className={`help-search__autosuggest__list-item ${
									i === cursor ? "selected" : ""
								}`}
								ref={(instance) => {
									refsColletion[i] = instance;
								}}
								role="presentation"
								aria-label={removeEllipsis(link.title, Link.desc)}
							>
								<Link
									href={link.url}
									analyticsLinkId={link.analyticsLinkId}
									handleClick={(e) => handleSelection(e, i)}
								>
									<span className="help-icon-container">
										<Icon
											icon={
												useNavigationSearchService
													? link.icon
													: extractIcon(link.webFontCode)
											}
										/>
									</span>
									<div className="help-link__details">
										<div
											aria-hidden="true"
											dangerouslySetInnerHTML={{
												__html: link.label || link.title,
											}}
										/>
										<div
											aria-hidden="true"
											className="help-link__details__description"
										>
											{fullWordsEllipsis(link.desc)}
										</div>
									</div>
								</Link>
							</li>
						))}
					</ul>
				)}
			</div>
			<DecorativeImage
				isDecorativeImage={decorativeImage?.length > 0}
				baseClass={`help-search__decorative-${decorativeMainClass}`}
				decorativeImage={decorativeImage}
				fairy={{ location: imageLocation, image: fairyImage }}
			/>
		</Fragment>
	);
};

export default Search;
